import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import moment from "moment";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";

import Panel from "../../../components/Panel";
import GlassOrderPrint from "./GlassOrderPrint";
import { getFullName } from "../../../helpers/formatters";
import { GlassOrderStatus } from "../../../enum/GlassOrder";
import { GlassOrderStatusChip } from "./GlassOrderStatus";
import { GlassOrder } from "../../../interfaces/GlassOrder";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import HandleGlassOrderStatus from "./HandleGlassOrderStatus";
import { PRESCRIPTION_COLS, PRESCRIPTION_ROWS } from "./GlassOrderCreateEdit";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { useDeleteGlassOrder, useGetGlassOrder } from "../../../hooks/query/useGlassOrders";

interface GlassOrderInfoProps {
  selectedRow?: GlassOrder | null;
  onClose?: () => void;
  onEditOpen?: () => void;
  onDeleteSuccess?: () => void;
  onStatusUpdated?: (updatedOrder: GlassOrder) => void;
}

const cellBorder = { border: 1, borderColor: "divider", padding: "6px 10px" };

export default function GlassOrderInfo({
  selectedRow,
  onClose,
  onEditOpen,
  onDeleteSuccess,
  onStatusUpdated
}: GlassOrderInfoProps): React.JSX.Element {
  const dispatch = useDispatch();
  const orderId = Number(selectedRow?.id);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [glassOrder, setGlassOrder] = useState<GlassOrder | null>(selectedRow || null);

  const deleteMutation = useDeleteGlassOrder();
  const { data: orderRes } = useGetGlassOrder(orderId, {
    enabled: !!orderId
  });

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(push("/assessment/glassOrders"));
    }
  };

  const handleDelete = async () => {
    if (!orderId) return;
    const res = await deleteMutation.mutateAsync(orderId);
    if (res?.success) {
      setDeleteDialogOpen(false);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
      handleClose();
    }
  };

  // effect
  useEffect(() => {
    if (orderRes?.data) {
      setGlassOrder(orderRes.data);
    }
  }, [orderRes?.data]);

  return (
    <Panel
      onClose={handleClose}
      titleJsx={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography>
              <Box component="span" sx={{ fontWeight: 600, fontSize: "16px", pr: 2 }}>
                Glass Order Information
              </Box>
            </Typography>
            <Box>{glassOrder?.status && <GlassOrderStatusChip status={glassOrder.status} />}</Box>
          </Box>

          <Box sx={{ display: "flex" }}>
            {glassOrder && <GlassOrderPrint glassOrder={glassOrder} />}

            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <IconButton
                sx={{ cursor: "pointer", padding: "5px" }}
                onClick={() => setDeleteDialogOpen(true)}
                title="Delete Order"
              >
                <Delete />
              </IconButton>
            </Box>

            {glassOrder && (
              <Box sx={{ display: "flex", alignItems: "baseline", ml: "8px" }}>
                <IconButton
                  sx={{ cursor: "pointer", padding: "5px" }}
                  onClick={() => {
                    if (onEditOpen) {
                      onEditOpen();
                    } else {
                      dispatch(push(`/assessment/glassOrders/${glassOrder.id}/edit`));
                    }
                  }}
                  title="Edit Order"
                >
                  <Edit />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      }
    >
      {deleteDialogOpen && (
        <OkhatiDialog
          title="Delete Glass Order"
          description="Are you sure you want to delete this glass order? This action cannot be undone."
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(false)}
          readMode={false}
        />
      )}

      {glassOrder ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {/* Client */}
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>Client</Typography>
            <Typography>{glassOrder?.client ? getFullName(glassOrder.client) : "-"}</Typography>
          </Box>

          {/* Order Date */}
          {glassOrder?.orderDate && (
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>
                Order Date
              </Typography>
              <Box>
                <Typography>
                  {convertADtoBS(new Date(glassOrder.orderDate)).formatted4} BS
                </Typography>
                <Typography sx={{ textTransform: "uppercase" }}>
                  {moment(glassOrder.orderDate).format("YYYY-MM-DD hh:mm a")}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Delivery Date (Delivered only) */}
          {glassOrder?.status === GlassOrderStatus.DELIVERED && glassOrder?.deliveryDate && (
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>
                Delivery Date
              </Typography>
              <Box>
                <Typography>
                  {convertADtoBS(new Date(glassOrder.deliveryDate)).formatted4} BS
                </Typography>
                <Typography sx={{ textTransform: "uppercase" }}>
                  {moment(glassOrder.deliveryDate).format("YYYY-MM-DD hh:mm a")}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Frame */}
          {glassOrder?.frame && (
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>Frame</Typography>
              <Typography>{glassOrder.frame}</Typography>
            </Box>
          )}

          {/* Lens */}
          {glassOrder?.lens && (
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>Lens</Typography>
              <Typography>{glassOrder.lens}</Typography>
            </Box>
          )}

          {/* Prescription */}
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>
              Glass Prescription
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TableContainer>
                <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.04)" }}>
                      <TableCell sx={{ ...cellBorder, fontWeight: 600, width: "60px" }}>
                        Eye
                      </TableCell>
                      {PRESCRIPTION_COLS.map((col) => (
                        <TableCell
                          key={col.key}
                          sx={{ ...cellBorder, fontWeight: 600, textAlign: "center" }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {PRESCRIPTION_ROWS.map((row) => {
                      const eyeData =
                        (glassOrder.prescription?.[row.key] as Record<
                          string,
                          string | number | null
                        >) || {};
                      return (
                        <TableRow key={row.key}>
                          <TableCell
                            sx={{
                              ...cellBorder,
                              fontWeight: 600,
                              backgroundColor: "rgba(0, 0, 0, 0.02)"
                            }}
                          >
                            {row.label}
                          </TableCell>
                          {PRESCRIPTION_COLS.map((col) => (
                            <TableCell key={col.key} sx={{ ...cellBorder, textAlign: "center" }}>
                              {eyeData[col.key] ? String(eyeData[col.key]) : "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Financial Breakdown */}
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>
              Total Amount
            </Typography>
            <Typography>Rs. {glassOrder.total ?? 0}</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>
              Advance Amount
            </Typography>
            <Typography>Rs. {glassOrder.advance ?? 0}</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>
              Balance Amount
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: (glassOrder.balance ?? 0) > 0 ? "error.main" : "success.main"
              }}
            >
              Rs. {glassOrder.balance ?? 0}
            </Typography>
          </Box>

          {/* Remarks / Note */}
          {glassOrder?.remarks && (
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>Note</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>{glassOrder.remarks}</Typography>
            </Box>
          )}

          {/* Status Handler */}
          <Box sx={{ display: "flex", alignItems: "baseline" }}>
            <Typography sx={{ width: "30%", flexShrink: 0, fontWeight: 600 }}>Status</Typography>
            <HandleGlassOrderStatus
              glassOrder={glassOrder}
              setGlassOrder={setGlassOrder}
              onStatusUpdated={onStatusUpdated}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ color: "text.secondary" }}>Loading glass order details...</Typography>
        </Box>
      )}
    </Panel>
  );
}
